/** The CCS Leader — standalone public workshop page at /workshops/the-ccs-leader.
 * Flyer hero + SEO/accessible page text, both enquiry CTAs feed the existing
 * booking flow tagged as "The CCS Leader". */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle, Users, HeartHandshake, Clock, Tag, CalendarClock } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";
import LegalFooter from "@/components/brand/LegalFooter";

const ENQUIRE_LINK = "/book?workshop=The%20CCS%20Leader#booking";

const PILLARS = [
  {
    icon: MessageCircle,
    title: "Communicate",
    text: "Finding your leadership voice and giving feedback with confidence, especially to people who were recently your peers.",
  },
  {
    icon: Users,
    title: "Collaborate",
    text: "Building trust and setting expectations without leaning on authority alone.",
  },
  {
    icon: HeartHandshake,
    title: "Serve",
    text: "Leading in a way that makes your team more effective, not just more managed.",
  },
];

const EnquireButton = ({ testid, className = "" }) => (
  <Link
    to={ENQUIRE_LINK}
    className={`inline-flex items-center gap-2 bg-[#C28A2E] text-[#2A1810] px-8 py-3.5 rounded-full text-sm font-bold tracking-wide hover:bg-[#E7B54D] transition-colors duration-300 ${className}`}
    data-testid={testid}
  >
    Enquire Now <ArrowRight className="w-4 h-4" />
  </Link>
);

export default function CCSLeaderPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "The CCS Leader — The CCS Approach";
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF5]" data-testid="ccs-leader-page">
      {/* Header */}
      <header className="border-b border-[#EBE3D2] bg-[#FDFBF5]/90 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="ccs-leader-header-logo">
            <LogoMark size={40} />
            <div className="leading-tight">
              <div className="font-display font-semibold text-[#2A1810] text-sm">The CCS Approach</div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Public Workshop</div>
            </div>
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            <Link to="/" className="text-[#6B5944] hover:text-[#5D3A1F]" data-testid="ccs-leader-nav-home">Home</Link>
            <Link to="/book#events" className="hidden sm:inline text-[#6B5944] hover:text-[#5D3A1F]" data-testid="ccs-leader-nav-events">Workshops &amp; Events</Link>
            <Link to={ENQUIRE_LINK} className="text-[#C28A2E] font-semibold hover:text-[#9A6B1E]" data-testid="ccs-leader-nav-enquire">Enquire</Link>
          </nav>
        </div>
      </header>

      {/* Hero — full flyer on the brand's dark stage */}
      <section className="bg-[#1E120B]" data-testid="ccs-leader-hero">
        <div className="max-w-4xl mx-auto px-0 sm:px-6 pt-0 sm:pt-10">
          <img
            src="/brand/ccs-leader-flyer.jpg"
            alt="The CCS Leader — a half-day workshop for first-time supervisors and newly promoted leaders. Your first step from supervisor to leader. TTD $350 per person, materials included."
            className="w-full sm:rounded-t-sm"
            data-testid="ccs-leader-flyer-image"
          />
        </div>
        <div className="max-w-4xl mx-auto px-6 pb-12 pt-8 text-center">
          <EnquireButton testid="ccs-leader-hero-enquire" />
        </div>
      </section>

      {/* Page text (SEO / accessibility) */}
      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
        <Link
          to="/book#events"
          className="inline-flex items-center gap-2 text-sm text-[#6B5944] hover:text-[#5D3A1F] transition-colors"
          data-testid="back-to-events"
        >
          <ArrowLeft size={15} strokeWidth={1.5} /> Public Workshops &amp; Events
        </Link>

        <div className="mt-10 text-xs uppercase tracking-[0.3em] font-semibold text-[#C28A2E]">Public Workshop · Now Booking</div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#2A1810]" data-testid="ccs-leader-title">
          The CCS Leader
        </h1>
        <p className="mt-3 text-lg text-[#C28A2E] font-medium italic" data-testid="ccs-leader-tagline">
          Your first step from supervisor to leader.
        </p>

        <h2 className="mt-10 font-display text-lg sm:text-xl font-semibold text-[#2A1810] leading-snug">
          What changes when yesterday's colleague becomes today's supervisor?
        </h2>
        <p className="mt-4 text-base sm:text-lg leading-[1.85] text-[#2A1810]/85">
          A half-day, hands-on workshop for first-time supervisors and newly promoted leaders.
          Because a title change doesn't make you a leader — how you communicate, how you build
          with people, and how you serve your team does.
        </p>

        <div className="mt-12 space-y-5">
          {PILLARS.map((p) => (
            <div key={p.title} className="flex items-start gap-4 bg-white border border-[#EBE3D2] rounded-xl p-5" data-testid={`ccs-leader-pillar-${p.title.toLowerCase()}`}>
              <div className="w-11 h-11 rounded-xl bg-[#C28A2E]/12 flex items-center justify-center shrink-0">
                <p.icon className="w-5 h-5 text-[#C28A2E]" strokeWidth={1.7} />
              </div>
              <div>
                <h3 className="font-display text-base font-semibold text-[#2A1810]">{p.title}</h3>
                <p className="text-sm text-[#6B5944] mt-1 leading-relaxed">{p.text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Details */}
        <div className="mt-12 bg-[#2A1810] rounded-2xl px-8 py-8" data-testid="ccs-leader-details">
          <div className="text-xs uppercase tracking-[0.3em] font-semibold text-[#F5D88A] mb-5">Details</div>
          <ul className="space-y-3 text-sm text-[#F0E6D2]/85">
            <li className="flex items-center gap-3"><Tag className="w-4 h-4 text-[#E7B54D] shrink-0" /> TTD $350 per person, materials included</li>
            <li className="flex items-center gap-3"><Clock className="w-4 h-4 text-[#E7B54D] shrink-0" /> Half-day workshop</li>
            <li className="flex items-center gap-3"><CalendarClock className="w-4 h-4 text-[#E7B54D] shrink-0" /> Date and location arranged on enquiry</li>
          </ul>
          <div className="mt-7">
            <EnquireButton testid="ccs-leader-details-enquire" />
          </div>
        </div>
      </main>

      <footer className="border-t border-[#EBE3D2] py-10 px-6">
        <p className="text-xs italic text-[#8B5A2B] text-center">"Let everything you do be done in love." — 1 Corinthians 16:14</p>
        <LegalFooter className="mt-6" />
      </footer>
    </div>
  );
}
